import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Grid } from '@material-ui/core';
import { LatestPipelineCard } from './LatestPipelineCard';
import { RecentRunsCard } from './RecentRunsCard';
import { RepositoryStandardCard } from './RepositoryStandardCard';
import { outcomeOf } from './outcome';
import type { Pipeline } from './types';
import { slugQuery, useInsights } from './useInsights';

const PROJECT_SLUG_ANNOTATION = 'github.com/project-slug';

/**
 * The CI/CD tab of a component's page — and of an API's page, which shares it
 * (specs/component-pipeline.md). Whether this component builds and deploys,
 * read server-side through the portal's GitHub App, so no reader needs a GitHub
 * login of their own.
 *
 * Read-only by design: nothing here starts a run or a deployment (AC-11). The
 * GitHub App cannot write, and a portal button that forwards to GitHub would be
 * a control whose feature is somewhere else.
 */
export const CiCdContent = () => {
  const { entity } = useEntity();
  const slug = entity.metadata.annotations?.[PROJECT_SLUG_ANNOTATION];
  const pipeline = useInsights<Pipeline>(
    slug ? `/pipeline?${slugQuery(slug)}` : undefined,
    {
      refreshWhile: data =>
        data.latestPipeline !== null &&
        outcomeOf(data.latestPipeline).kind === 'running',
    },
  );

  if (!slug) {
    return (
      <InfoCard title="No CI/CD available for this entity">
        Add a <code>github.com/project-slug</code> annotation to this
        component's <code>catalog-info.yaml</code> to enable CI/CD.
      </InfoCard>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <LatestPipelineCard slug={slug} pipeline={pipeline} />
      </Grid>
      <Grid item xs={12}>
        <RepositoryStandardCard slug={slug} />
      </Grid>
      <Grid item xs={12}>
        <RecentRunsCard
          slug={slug}
          defaultBranch={
            pipeline.loading || pipeline.error
              ? undefined
              : pipeline.data?.defaultBranch
          }
        />
      </Grid>
    </Grid>
  );
};
